import { BadgeTextSource } from "sharedTypes/badge"
import {
  PersonInfo, SeatingAssignment, EventGroup, DioboxEvent,
} from "sharedTypes/index"
import SAMPLE_GUEST_DATA from "utilities/sampleGuestData"
import { SAMPLE_EVENT_DATA } from "components/BadgeBuilder/utils/sampleData"

export type GuestData = {
  personInfo: PersonInfo
  seatingAssignment: SeatingAssignment
  eventGroups: EventGroup[]
}
export type EventData = DioboxEvent

/**
 * Resolves a badge text source to the actual display value
 * Works with actual Guest API type
 */
export const resolveBadgeTextSource = (
  source: BadgeTextSource,
  staticText: string | undefined,
  guestData?: GuestData,
  eventData?: EventData,
): string => {
  const guest = guestData || SAMPLE_GUEST_DATA
  const event = eventData || SAMPLE_EVENT_DATA

  switch (source) {
    case BadgeTextSource.EVENT_NAME:
      return event.title || ""

    case BadgeTextSource.GUEST_FULL_NAME: {
      // Special case: if using sample data, show "Full Name" directly
      if (guest === SAMPLE_GUEST_DATA) {
        return "Full Name"
      }

      const firstName = guest.personInfo?.firstName || ""
      const lastName = guest.personInfo?.lastName || ""

      return `${firstName} ${lastName}`.trim()
    }

    case BadgeTextSource.GUEST_FIRST_NAME:
      return guest.personInfo?.firstName || ""

    case BadgeTextSource.GUEST_LAST_NAME:
      return guest.personInfo?.lastName || ""

    case BadgeTextSource.GUEST_COMPANY:
      return guest.personInfo?.companyName || ""

    case BadgeTextSource.GUEST_JOB_TITLE:
      return guest.personInfo?.companyTitle || ""

    case BadgeTextSource.GUEST_EMAIL:
      return guest.personInfo?.email || ""

    case BadgeTextSource.GUEST_TABLE_ROW:
      return guest.seatingAssignment?.seatingTableTitle || ""

    case BadgeTextSource.GUEST_TABLE_ROW_SEAT: {
      const tableTitle = guest.seatingAssignment?.seatingTableTitle
      if (!tableTitle) return ""
      const seatNumber = guest.seatingAssignment?.seat

      return seatNumber ? `${tableTitle} \u2014 Seat: ${seatNumber}` : tableTitle
    }

    case BadgeTextSource.GUEST_CITY_STATE: {
      const city = guest.personInfo?.city
      const state = guest.personInfo?.address?.state
      if (city && state) {
        return `${city}, ${state}`
      }
      if (city) return city
      if (state) return state

      return ""
    }

    case BadgeTextSource.TICKET_NAME:
      return guest.personInfo?.formSubmissionLineItems
        ?.filter((li) => li.formType === "Ticket")
        .map((li) => li.name)
        .join(", ") || ""

    case BadgeTextSource.FORM_NAME:
      return [...new Set(
        guest.personInfo?.formSubmissionLineItems
          ?.slice()
          .sort((a, b) => (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            || a.name.localeCompare(b.name)
          ))
          .map((li) => li.name) || [],
      )].join(", ") || ""

    case BadgeTextSource.GUEST_LIST:
      return guest.eventGroups?.map((g) => g.title).join(", ") || ""

    default:
      return `[${source}]`
  }
}
